import { TableColumnsType } from "antd";
import { BasePagination } from "api/interfaces/commonInterface.interface";
import { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteExpandTable from "components/blocks/table/InfiniteExpandTable";
import { useInfiniteQuery } from "react-query";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import {
  AccountInfo,
  GetAccountListResponse,
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { getAccountTreeList } from "api/accountAPI";
import { ExpandableConfig, TableRowSelection } from "antd/es/table/interface";
import { useAppSelector } from "redux/hooks";
import { checkIsDoubleOrgTree } from "utils/AccountUtil";
import { useIsMobile } from "components/MediaQuery";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";

export default function AccountExpandContainer({
  selectedData,
  columns,
  queryInfo,
  expandable,
  rowSelection,
}: {
  selectedData: AccountInfo & { key?: string };
  columns: TableColumnsType<AccountInfo>;
  queryInfo: BasePagination;
  expandable?: ExpandableConfig<AccountInfo>;
  rowSelection?: TableRowSelection<AccountInfo>;
}) {
  const isMobile = useIsMobile();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { accountId } = selectedData;

  const isDoubleTree = useMemo(
    () =>
      checkIsDoubleOrgTree({
        accountLevel: selectedAccount.accountLevel,
        selectedLevel: selectedData.level,
        parentLevel: selectedData.parentLevel,
        rowKey: selectedData.key,
      }),
    [selectedAccount.accountLevel, selectedData]
  );

  const [expandQueryInfo, setExpandQueryInfo] = useState<BasePagination>({
    ...baseQueryInfo,
    sortType: "name",
  });

  const stableQueryKey = useQueryKey({ ...expandQueryInfo, pageNum: 0 });

  const {
    data,
    isLoading: isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(
    ["account", stableQueryKey, accountId],
    ({ pageParam = 0 }) =>
      getAccountTreeList({
        payload: {
          ...expandQueryInfo,
          pageNum: pageParam,
        },
        accountId,
        isSelf: false,
      }),
    {
      enabled: !!accountId,
      refetchOnWindowFocus: false,
      // When a user collapses and re-expands a row, this component can remount
      // with cached data; ensure the treeList request is fired consistently.
      refetchOnMount: "always",
      retry: 0,
      getNextPageParam: (lastPage: GetAccountListResponse | undefined) => {
        if (!lastPage?.page) return undefined;

        const { total, pageNum, pageLimit } = lastPage.page;
        const nextPage = pageNum + 1;

        if (nextPage * pageLimit < total) return nextPage;
        return undefined;
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: [
      "organizationListUpdated",
      "loginAccessUpdated",
    ] as StompDateSignalKey[],
    refetch: refetch,
  });

  const loadMore = useCallback(() => {
    if (!hasNextPage) return;
    if (isFetchingNextPage) return;
    if (isInitialLoading) return;

    fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isInitialLoading, fetchNextPage]);

  const list = useMemo(() => {
    const parentRowKey = String(selectedData.key ?? selectedData.accountId);

    return (data?.pages || [])
      .flatMap((page: GetAccountListResponse) => page.result ?? [])
      .map((account: AccountInfo) => {
        return {
          ...account,
          key: `${parentRowKey}:${account.accountId}`,
        };
      });
  }, [data, selectedData.accountId, selectedData.key]);

  useEffect(() => {
    setExpandQueryInfo((prev) => ({
      ...prev,
      sortDirection: queryInfo.sortDirection,
      sortType: queryInfo.sortType !== "name" ? queryInfo.sortType : "name",
      pageNum: 0,
    }));
  }, [queryInfo.sortType, queryInfo.sortDirection]);

  return (
    <InfiniteExpandTable
      data={list}
      loadMore={loadMore}
      isLoading={isInitialLoading}
      hasMore={hasNextPage}
      columns={columns}
      tableWrapperHeight={0}
      leftPadding={isMobile ? 10 : 50}
      expandable={expandable}
      isDoubleTree={isDoubleTree}
      rowKey="key"
      rowSelection={rowSelection}
    />
  );
}
